import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { KeyRound, Mail, MessageSquare } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { useLang } from '@/lib/i18n';
import { Logo } from '@/components/Logo';

export default function ForgotPasswordPage() {
  const { t } = useLang();
  const nav = useNavigate();
  const [step, setStep] = useState(1); // 1: email+canal, 2: code+nouveau mdp
  const [email, setEmail] = useState('');
  const [channel, setChannel] = useState('email');
  const [demoCode, setDemoCode] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const requestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post('/auth/forgot-password', { email, channel });
      setDemoCode(r.data.reset_code_demo || '');
      setStep(2);
      toast.success(channel === 'email' ? 'Code envoyé par email (simulé)' : 'Code envoyé par SMS (simulé)');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\u2019envoi du code');
    } finally { setLoading(false); }
  };

  const doReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { email, code, new_password: newPassword });
      toast.success(t('forgot.success'));
      nav('/login');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Code invalide');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F7FAFF] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-6">
          <Logo size="xl" variant="default" />
        </div>
        <Card className="p-6 md:p-8">
          <div className="flex items-center gap-3 mb-1">
            <span className="h-10 w-10 rounded-xl bg-[#EAF3FF] text-[#1677F2] flex items-center justify-center">
              <KeyRound size={20} />
            </span>
            <div>
              <h1 className="text-xl font-bold text-[#0B1220]">{t('forgot.title')}</h1>
              <p className="text-xs text-slate-500">{t('forgot.subtitle')}</p>
            </div>
          </div>

          {demoCode && (
            <div className="mt-4 rounded-xl bg-[#FFF6C7] border border-[#F7D618]/50 px-4 py-3 text-sm text-[#0B1220]" data-testid="forgot-demo-code-banner">
              <strong>{t('forgot.demoBanner')}</strong>{' '}
              <span className="font-bold text-lg tracking-widest" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{demoCode}</span>
            </div>
          )}

          {step === 1 ? (
            <form onSubmit={requestCode} className="mt-6 space-y-4">
              <div>
                <Label>{t('auth.email')}</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email-input" />
              </div>
              <div>
                <Label className="mb-2 block">{t('forgot.channel.label')}</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setChannel('email')}
                    className={`flex items-center justify-center gap-2 rounded-xl border-2 p-3 text-sm font-semibold transition-colors duration-150 ${channel === 'email' ? 'border-[#1677F2] bg-[#EAF3FF] text-[#0E3F80]' : 'border-[#E2E8F0] text-slate-600 hover:border-slate-300'}`}
                    data-testid="forgot-channel-email"
                  >
                    <Mail size={16} /> {t('forgot.channel.email')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setChannel('sms')}
                    className={`flex items-center justify-center gap-2 rounded-xl border-2 p-3 text-sm font-semibold transition-colors duration-150 ${channel === 'sms' ? 'border-[#1677F2] bg-[#EAF3FF] text-[#0E3F80]' : 'border-[#E2E8F0] text-slate-600 hover:border-slate-300'}`}
                    data-testid="forgot-channel-sms"
                  >
                    <MessageSquare size={16} /> {t('forgot.channel.sms')}
                  </button>
                </div>
              </div>
              <Button type="submit" className="w-full bg-[#1677F2] hover:bg-[#0F5FCC]" disabled={loading} data-testid="forgot-send-code-btn">
                {loading ? t('common.loading') : t('forgot.sendCode')}
              </Button>
            </form>
          ) : (
            <form onSubmit={doReset} className="mt-6 space-y-4">
              <div>
                <Label>{t('forgot.code.label')}</Label>
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  maxLength={6}
                  inputMode="numeric"
                  placeholder="000000"
                  className="text-center text-xl tracking-[0.4em] font-bold"
                  required
                  data-testid="forgot-code-input"
                />
              </div>
              <div>
                <Label>{t('forgot.newPassword')}</Label>
                <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} minLength={6} required data-testid="forgot-new-password-input" />
              </div>
              <Button type="submit" className="w-full bg-[#1677F2] hover:bg-[#0F5FCC]" disabled={loading} data-testid="forgot-reset-btn">
                {loading ? t('common.loading') : t('forgot.reset')}
              </Button>
              <button type="button" onClick={() => setStep(1)} className="w-full text-xs text-slate-500 hover:text-slate-700" data-testid="forgot-back-step1">
                ← {t('forgot.sendCode')}
              </button>
            </form>
          )}

          <p className="text-center text-sm text-slate-600 mt-6">
            <Link to="/login" className="text-[#1677F2] font-medium" data-testid="forgot-back-login-link">{t('forgot.backToLogin')}</Link>
          </p>
        </Card>
      </div>
    </div>
  );
}
